import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import {
  getOrBuildCache,
  buildRunwayProjectionPoints,
} from "../../utils/chart/financial-cache.utils";
import {
  formatChartPeriodLabel,
  formatSignedCurrency,
  getChartGranularity,
  getChartPeriodCountLabel,
  getChartPeriodData,
  resolveProjectEndChartMarker,
} from "../../utils/chart/chart-period.utils";
import ProjectEndReferenceLine from "./ProjectEndReferenceLine";
import type { Revenue, Expense } from "../../types/startup.types";
import { CHART_COLORS } from "@/constants/chart-colors";

// Using main type definitions from startup.types.ts

interface EventData {
  id: string;
  date: Date;
  type: "funding" | "milestone" | "launch" | "asset" | "custom";
  priority: "high" | "medium" | "low";
  description: string;
  amount?: number;
  icon?: string;
  color?: string;
  // Asset-specific fields
  monthlyAppreciationRate?: number;
  minimumValue?: number;
}

interface ChartData {
  startDate: Date;
  endDate: Date;
  projectEndDate?: Date;
  initialBalance: number;
  expenses: Expense[];
  revenues?: Revenue[];
  events: EventData[];
}

interface RunwayProjectionProps {
  data: ChartData;
  visibleDateRange: {
    startDate: Date;
    endDate: Date;
  };
  height?: number;
}

const RunwayProjectionChart: React.FC<RunwayProjectionProps> = ({
  data,
  visibleDateRange,
  height = 250,
}) => {
  // Use the optimized cache for date range data - massive performance improvement!
  const { projectionData, granularity, projectEndMarker } = useMemo(() => {
    // console.log('RunwayProjectionChart: Building chart data with cache...');
    
    // Get or build the financial cache (only computed once per data change)
    const cache = getOrBuildCache(data);
    
    // Extract data for the visible date range (O(n) where n = months in range, not total data)
    const rangeData = getChartPeriodData(
      cache,
      data as Parameters<typeof getChartPeriodData>[1],
      visibleDateRange.startDate,
      visibleDateRange.endDate,
    );
    const granularity = getChartGranularity(
      visibleDateRange.startDate,
      visibleDateRange.endDate,
    );

    const projectionData = buildRunwayProjectionPoints(rangeData).map(
      (point, index) => ({
        month: formatChartPeriodLabel(rangeData[index], "short"),
        actualCase: point.actualCase,
        conservativeCase: point.conservativeCase,
        optimisticCase: point.optimisticCase,
        date: new Date(point.month),
        suggestedFunding: point.suggestedFunding,
        fundingAmount: point.fundingAmount,
        assetAcquisitionValue: point.assetAcquisitionValue,
      }),
    );

    const projectEndMarker = resolveProjectEndChartMarker(
      data.projectEndDate,
      visibleDateRange.startDate,
      visibleDateRange.endDate,
      rangeData.map((periodData) => ({
        label: formatChartPeriodLabel(periodData, "short"),
        periodStart: periodData.periodStart,
        periodEnd: periodData.periodEnd,
      })),
    );

    return { projectionData, granularity, projectEndMarker };
  }, [data, visibleDateRange]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      
      return (
        <div className="bg-surface-elevated text-white text-xs px-3 py-2 rounded-lg shadow-glow">
          <p className="font-semibold mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {formatSignedCurrency(entry.value)}
            </p>
          ))}
          {data.fundingAmount && (
            <p className="text-blue-400 mt-1">Funding: ${data.fundingAmount.toLocaleString()}</p>
          )}
          {data.assetAcquisitionValue && (
            <p className="text-purple-400">Asset Acquired: ${data.assetAcquisitionValue.toLocaleString()}</p>
          )}
          {data.suggestedFunding && (
            <p className="text-yellow-400 mt-1">⚠️ Suggested Funding: ${data.suggestedFunding.toLocaleString()}</p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="sh-panel overflow-hidden">
      <div className="border-b border-white/10 px-4 py-2 bg-surface-secondary/60">
        <h4 className="text-sm font-medium text-white/75">
          Runway Projection (
          {getChartPeriodCountLabel(projectionData.length, granularity)})
        </h4>
        <div className="flex items-center gap-4 mt-1 text-xs text-white/45">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-brand rounded"></div>
            <span>Actual</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-expense rounded"></div>
            <span>Conservative</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-500 rounded"></div>
            <span>Optimistic</span>
          </div>
        </div>
      </div>
      <div className="p-4">
        <ResponsiveContainer width="100%" height={height}>
          <LineChart
            data={projectionData}
            margin={{
              top: 10,
              right: 30,
              left: 20,
              bottom: 10,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis 
              dataKey="month" 
              tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.5)' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis 
              tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.5)' }}
              axisLine={false}
              tickLine={false}
              domain={["auto", "auto"]}
              tickFormatter={formatSignedCurrency}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={0} stroke="rgba(255,255,255,0.25)" strokeWidth={2} strokeDasharray="5 5" />
            {projectEndMarker ? (
              <ProjectEndReferenceLine periodLabel={projectEndMarker.periodLabel} />
            ) : null}
            <Line
              type="monotone"
              dataKey="actualCase"
              stroke="#3b82f6"
              strokeWidth={3}
              dot={false}
              name="Actual Case"
            />
            <Line
              type="monotone"
              dataKey="conservativeCase"
              stroke={CHART_COLORS.cashNegative}
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              name="Conservative Case"
            />
            <Line
              type="monotone"
              dataKey="optimisticCase"
              stroke={CHART_COLORS.cashPositive}
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              name="Optimistic Case"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RunwayProjectionChart;
